import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Star, HelpCircle, Mail, Send, Check, Loader2, Phone } from 'lucide-react';
import { pluginsFor, pluginEnabled } from '../data/businessCatalog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const INPUT_CLS = 'bg-[var(--bg-input)] border-[var(--border-input)] text-[var(--text-primary)] placeholder:text-[var(--text-faint)]';

// Shared public sections (FAQ, reviews, contact form, contact note) gated by the
// page's plugins. Each section is rendered as an ordered flex item (via the `ord`
// prop) so the owner's saved "section order" applies on the public page. Meant to
// be placed INSIDE the page's ordered flex column.
export default function BusinessExtras({ template, kind = 'general', config, userId, pageId, ord }) {
  const o = typeof ord === 'function' ? ord : () => 99;
  const [contact, setContact] = useState({ name: '', email: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const avail = pluginsFor(template, kind);
  const on = (id) => avail.includes(id) && pluginEnabled(template, config?.plugins, id, kind);
  const faq = config?.faq || [];
  const reviews = config?.reviews || [];
  const contactNote = config?.contact_note;

  const submit = async () => {
    if (!contact.name.trim() || !contact.message.trim()) { toast.error('Aizpildi vārdu un ziņu'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/business-page/${userId}/contact`, { ...contact, page_id: pageId || undefined });
      setSent(true); setContact({ name: '', email: '', phone: '', message: '' });
      toast.success('Ziņa nosūtīta!');
    } catch { toast.error('Neizdevās nosūtīt'); } finally { setSending(false); }
  };

  const showFaq = on('faq') && faq.length > 0;
  const showReviews = on('reviews') && reviews.length > 0;
  const showContact = on('contact_form');
  if (!showFaq && !showReviews && !showContact && !contactNote) return null;

  return (
    <>
      {showFaq && (
        <section data-testid="faq-section" style={{ order: o('faq') }}>
          <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><HelpCircle className="w-5 h-5 text-[var(--accent)]" /> Biežāk uzdotie jautājumi</h2>
          <div className="space-y-3">
            {faq.map((f, i) => (
              <details key={i} className="group rounded-2xl border border-[var(--border-card)] bg-[var(--bg-card)] px-5 py-4 shadow-sm" data-testid={`faq-item-${i}`}>
                <summary className="cursor-pointer list-none flex items-center justify-between gap-3 text-[var(--text-primary)] font-medium">
                  {f.q}
                  <span className="text-[var(--accent)] text-xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                {f.a && <p className="text-[var(--text-muted)] text-sm mt-3 leading-relaxed">{f.a}</p>}
              </details>
            ))}
          </div>
        </section>
      )}

      {showReviews && (
        <section data-testid="reviews-section" style={{ order: o('reviews') }}>
          <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Star className="w-5 h-5 text-[var(--accent)]" /> Atsauksmes</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {reviews.map((r, i) => (
              <div key={i} className="rounded-2xl border border-[var(--border-card)] bg-[var(--bg-card)] p-5 shadow-sm" data-testid={`review-${i}`}>
                <div className="flex items-center gap-0.5 mb-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-4 h-4 ${n <= (r.rating || 5) ? 'text-[var(--accent)] fill-[var(--accent)]' : 'text-[var(--text-faint)]'}`} />
                  ))}
                </div>
                {r.text && <p className="text-[var(--text-muted)] text-sm leading-relaxed">“{r.text}”</p>}
                <p className="text-[var(--text-faint)] text-xs mt-3 font-semibold">{r.author}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {showContact && (
        <section data-testid="contact-section" style={{ order: o('contact') }}>
          <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Mail className="w-5 h-5 text-[var(--accent)]" /> Sazinies ar mums</h2>
          <div className="rounded-3xl border border-[var(--border-card)] bg-[var(--bg-card)] p-6 max-w-2xl shadow-sm">
            {sent ? (
              <div className="text-center py-6" data-testid="contact-sent">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-500/20 flex items-center justify-center"><Check className="w-6 h-6 text-emerald-400" /></div>
                <p className="text-[var(--text-primary)] font-semibold">Paldies! Ziņa nosūtīta.</p>
                <p className="text-[var(--text-faint)] text-sm mt-1">Mēs ar tevi sazināsimies pēc iespējas ātrāk.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Vārds *" className={INPUT_CLS} data-testid="contact-name" />
                  <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Tālrunis" className={INPUT_CLS} data-testid="contact-phone" />
                </div>
                <Input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="E-pasts" className={INPUT_CLS} data-testid="contact-email" />
                <Textarea value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} placeholder="Tava ziņa *" rows={4} className={INPUT_CLS} data-testid="contact-message" />
                <button onClick={submit} disabled={sending}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] disabled:opacity-60" data-testid="contact-submit">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Nosūtīt ziņu
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {contactNote && (
        <p className="text-[var(--text-faint)] text-sm flex items-start gap-2" style={{ order: o('contact_note') }} data-testid="contact-note">
          <Phone className="w-4 h-4 mt-0.5 shrink-0" /> {contactNote}
        </p>
      )}
    </>
  );
}
